/**
 * WebAudio synthesis: engine, tyres, impacts, announcer, soundtrack, weather ambience.
 * The soundtrack starts with authored tracks, then hands off to the generated adaptive
 * music. Sound effects and the remaining live layers are still generated in code.
 *
 * OWNER: `audio` — ARCHITECTURE.md §3. Publishes AudioRequests on ctx.audio (§4.9).
 *
 * ================================ THE HARNESS RULE =================================
 * WebAudio requires a user gesture. The headless harness never makes one, so this subsystem
 * is built to be a TRUE no-op there: `AudioGraph.disabled` is set when ctx.isHarness, the
 * gesture listeners are never bound, no AudioContext is ever constructed, and every voice
 * class short-circuits before touching a WebAudio API. What still runs is the pure-JS model
 * layer — engine rpm smoothing, the announcer queue, music form position, the sfx call
 * counters — because harnessStats() has to tell a critic what the audio subsystem WOULD be
 * doing, and because that state must be a deterministic function of the fixed-step count.
 * ===================================================================================
 *
 * `audio` is registered in UPDATE_ORDER only (src/core/registry.js), so fixedUpdate() is
 * never called on it. Everything happens in update(dt, alpha), which is correct: audio is
 * presentation. setEngine() is called from vehicle.fixedUpdate and only stores values.
 */

import { EV } from '../core/bus.js';
import { AUDIO_TUNING as T } from './tuning.js';
import { RandTable } from './dsp.js';
import { bakeBank, toAudioBuffers } from './banks.js';
import { AudioGraph } from './graph.js';
import { EngineVoice } from './engine.js';
import { TyreVoice } from './tyres.js';
import { SprayVoice } from './spray.js';
import { Announcer } from './announcer.js';
import { Soundtrack } from './soundtrack.js';
import { Ambience } from './ambience.js';
import { VoicePool } from './voices.js';

/** How many uniforms to pre-draw from the seeded fork. Big enough for the whole bake. */
const RAND_TABLE_SIZE = 1 << 16;

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

export class AudioSystem {
  static id = 'audio';

  constructor(ctx) {
    this.ctx = ctx;
    this.id = 'audio';

    this.frozen = false;
    this.baked = null;
    this.bakeMs = 0;

    // Scratch, allocated once. update() must not allocate (N9).
    this._pos = { x: 0, y: 0, z: 0 };
    this._weather = 'thaw';
    this._phase = 'title';
    this._lastHornFrame = -1;
  }

  // ------------------------------------------------------------------ boot

  async init() {
    const ctx = this.ctx;

    // ARCHITECTURE.md §6 — fork once in init(), draw only from the fork. Every uniform the
    // whole subsystem will ever need is drawn HERE, into a table, so RNG consumption is
    // identical whether or not an AudioContext is ever created. That is what makes "audio
    // silently absent in the harness, audible in the game" a determinism-safe difference.
    this.rng = ctx.rng.fork('audio');
    this.randValues = new Float32Array(RAND_TABLE_SIZE);
    for (let i = 0; i < RAND_TABLE_SIZE; i++) this.randValues[i] = this.rng.next();

    const params = typeof location !== 'undefined'
      ? new URLSearchParams(location.search) : new URLSearchParams('');
    const forced = params.get('audio');
    this.forceOn = forced === '1';
    this.muteParam = params.get('mute') === '1' || forced === '0';

    // Silent in the harness unless explicitly forced with ?audio=1.
    const disabled = this.muteParam || (ctx.isHarness && !this.forceOn);

    this.graph = new AudioGraph({ disabled, onStart: (g) => this._onGraphStart(g) });

    // Separate cursors over the same table so the bake, the music humanisation and the sfx
    // rate jitter cannot perturb one another's stream.
    this.bakeRand = new RandTable(this.randValues);
    this.musicRand = new RandTable(this.randValues);
    this.musicRand.seekTo(40009);
    this.sfxRand = new RandTable(this.randValues);
    this.sfxRand.seekTo(50021);

    this.engine = new EngineVoice(this.graph);
    this.tyres = new TyreVoice(this.graph);
    this.spray = new SprayVoice(this.graph);
    this.announcer = new Announcer(this.graph, (s, a) => this.graph.duck(s, a));
    this.music = new Soundtrack(this.graph, this.musicRand);
    this.ambience = new Ambience(this.graph);
    this.voices = new VoicePool(this.graph, this.sfxRand);

    this._publish();
    this._subscribe();

    // Bake the one-shot bank HERE, not on the first click. It costs a few hundred
    // milliseconds of straight-line DSP, and doing it inside the gesture handler put a
    // visible 0.7 s hitch on the exact frame the player pressed go (measured). At init it
    // is just another procedural asset alongside everyone else's textures. Skipped entirely
    // when audio is disabled, so the harness pays nothing.
    if (!disabled) {
      this.bakeRand.seekTo(0);
      this.baked = bakeBank(this.bakeRand, T.renderRate);
      this.bakeMs = +this.baked.ms.toFixed(1);
      this.graph.baked = this.baked;
    }

    // Arm the gesture listeners. No-op when disabled, so the harness never binds anything.
    this.graph.armGesture();
  }

  /** Publish the AudioRequests interface (ARCHITECTURE.md §4.9) onto ctx. */
  _publish() {
    const self = this;
    this.api = {
      /** Fire a one-shot. opts: {pos, gain, rate, variation}. */
      sfx(key, opts) { return self.voices.play(key, opts); },

      /** Queue an announcer bark. Higher priority interrupts a lower one mid-word. */
      announcer(line, priority) { return self.announcer.say(line, priority == null ? 1 : priority); },

      /** Called every fixed step by vehicle. Stores only — no audio work in the sim path. */
      setEngine(msg) {
        if (!msg) return;
        self.engine.set(msg.rpm || 0, clamp01(msg.load || 0), Math.abs(msg.speed || 0));
      },

      setAmbience(weather, density) { self.ambience.set(weather, density); },

      /** 'start' | 'stop' | 'auto' | 'intensity:0..1' */
      music(cmd) { self.music.command(cmd); },

      duck(ms, amount) {
        self.graph.duck((ms == null ? T.duck.defaultMs : ms) / 1000,
          amount == null ? T.duck.defaultAmount : amount);
      },

      // ---- extras beyond §4.9. Other owners may use them; nothing depends on them.
      setMuted(m) { self.graph.setMuted(!!m); },
      get isLive() { return self.graph.live; },
      get isMuted() { return self.graph.muted; },
      /** Force the context to start. Only succeeds from inside a user gesture. */
      resume() { return self.graph.start(); },
    };
    this.ctx.audio = this.api;
  }

  /**
   * Build every persistent node. Runs exactly once, the first time the player touches an
   * input (the WebAudio gesture requirement). The bank is already baked by init(), so all
   * that happens inside the gesture handler is node construction and a buffer copy.
   */
  _onGraphStart(g) {
    if (!this.baked) {
      // Only reachable if something started the context on a build where init() decided
      // audio was disabled — e.g. the offline verification rig.
      this.bakeRand.seekTo(0);
      this.baked = bakeBank(this.bakeRand, T.renderRate);
      this.bakeMs = +this.baked.ms.toFixed(1);
    }
    g.baked = this.baked;
    if (!g.buffers) g.buffers = toAudioBuffers(g.actx, this.baked);

    this.engine.build();
    this.tyres.build();
    this.spray.build();
    this.announcer.build();
    this.music.build();
    this.ambience.build();
    this.voices.build();

    // Catch the graph up to wherever the model already is.
    this.ambience.set(this._weather, 1);
    if (this._phase === 'playing') this.music.start();
    console.info(`[audio] context started at ${g.actx.sampleRate} Hz; ` +
      `${this.baked.oneShots.size} one-shots baked at init in ${this.bakeMs} ms`);
  }

  // ------------------------------------------------------------------ events

  _subscribe() {
    const bus = this.ctx.bus;
    this._subs = [];
    const on = (t, fn) => { bus.on(t, fn); this._subs.push([t, fn]); };

    // ---- THE PAYOFF. gameplay calls sfx('repairComplete') inline for the trowel-slap
    // transient; the chime layer is chosen HERE, from the event, because only the event
    // knows whether it was perfect. The two land within one fixed step of each other and
    // read as a single composite sound.
    on(EV.REPAIR_COMPLETED, (p) => {
      this.voices.play(p && p.perfect ? 'chimePerfect' : 'chime', { gain: 1 });
      this.graph.duck(T.events.completeDuckMs / 1000, T.events.completeDuckAmount);
    });

    // ---- THE THUMP. gameplay fires the sfx; we punch a hole in the bed so it lands, and
    // the announcer takes it personally.
    on(EV.POTHOLE_THUMPED, () => {
      this.graph.duck(T.events.thumpDuckMs / 1000, T.events.thumpDuckAmount);
      this.announcer.say('thump', 2);
    });

    on(EV.GAME_TIME_EXTENDED, () => { this.voices.play('timeBell'); });

    on(EV.REPAIR_FAILED, () => { this.voices.play('sprayEnd'); });

    on(EV.GAME_STARTED, (p) => {
      if (p && p.weather) { this._weather = p.weather; this.ambience.set(p.weather, 1); }
      this.announcer.say('go', 3);
      this.music.start();
    });

    on(EV.GAME_OVER, () => {
      this.announcer.say('gameOver', 4);
      this.music.stop();
    });

    on(EV.GAME_PHASE_CHANGED, (p) => {
      if (!p) return;
      this._phase = p.to;
      if (p.to === 'playing') this.music.start();
      else if (p.to === 'result' || p.to === 'title') this.music.stop();
    });

    on(EV.GAME_TIME_WARNING, (p) => {
      // The beep comes from gameplay's own sfx call; only the meaningful marks get a bark.
      if (p && p.secondsLeft != null && p.secondsLeft <= 10 && p.secondsLeft >= 5) {
        this.announcer.say('timeWarning', 2);
      }
    });

    on(EV.WORLD_WEATHER_CHANGED, (p) => {
      if (!p || !p.weather) return;
      this._weather = p.weather;
      this.ambience.set(p.weather, 1);
    });

    on(EV.VEHICLE_LANDED, (p) => {
      if (!p || p.impactSpeed == null || p.impactSpeed < T.events.landMinSpeed) return;
      const g = clamp01(p.impactSpeed / T.events.landImpactFull);
      let pos = null;
      if (p.position) { this._pos.x = p.position.x; this._pos.y = p.position.y; this._pos.z = p.position.z; pos = this._pos; }
      this.voices.play('landing', { pos, gain: 0.35 + 0.65 * g, rate: 1.1 - 0.2 * g });
    });

    on(EV.VEHICLE_GEAR_CHANGED, () => { this.voices.play('gearShift', { gain: T.engine.shiftGain }); });

    // vehicle already calls sfx('vehicle.impact') for its own crunch, so we only add the
    // bed duck here — playing a second crunch would double the hit.
    on(EV.VEHICLE_COLLIDED, (p) => {
      const imp = p ? clamp01((p.impulse || 0) / T.events.collideImpulseFull) : 0;
      if (imp > 0.35) this.graph.duck(0.18, 0.12 * imp);
    });

    on(EV.TRAFFIC_HIT, (p) => {
      if (!p || !p.position) return;
      this._pos.x = p.position.x; this._pos.y = p.position.y; this._pos.z = p.position.z;
      this.voices.play('crunch', {
        pos: this._pos,
        gain: clamp01(0.3 + (p.impulse || 0) / T.events.collideImpulseFull),
      });
    });

    on(EV.TRAFFIC_NEAR_MISS, (p) => {
      if (!p || !p.position) return;
      this._pos.x = p.position.x; this._pos.y = p.position.y; this._pos.z = p.position.z;
      const close = clamp01(1 - (p.gap || 0) / T.events.nearMissGap);
      this.voices.play('whoosh', { pos: this._pos, gain: 0.35 + 0.65 * close });
    });
  }

  // ------------------------------------------------------------------ frame

  fixedUpdate() { /* audio is presentation only — see registry.UPDATE_ORDER */ }

  /**
   * Presentation step. Budget 0.3 ms p95.
   *
   * With the graph dark this is a handful of scalar smoothing operations and a queue poll,
   * which is what keeps the harness clean and cheap.
   */
  update(dt, alpha) {           // eslint-disable-line no-unused-vars
    const ctx = this.ctx;
    const d = dt > 0.25 ? 0.25 : (dt > 0 ? dt : 1 / 60);

    const player = ctx.player;
    const gp = ctx.gameplay;

    // ---- listener frame from the camera. Read straight off the world matrix so we need
    // neither a THREE import nor a scratch Vector3.
    if (this.graph.live) {
      const e = ctx.camera.matrixWorld.elements;
      this.voices.setListener(e[12], e[13], e[14], -e[8], -e[10]);
    }

    // ---- engine
    this.engine.update(d, !!player.isAirborne);

    // ---- tyres. Take the surface from any grounded wheel; all four agree almost always,
    // and when they do not, the last one wins, which is fine for a 40 ms crossfade.
    let surface = 'road';
    let grounded = 0;
    const wc = player.wheelContacts;
    if (wc) {
      for (let i = 0; i < wc.length; i++) {
        if (wc[i] && wc[i].grounded) { grounded = 1; if (wc[i].surface) surface = wc[i].surface; }
      }
    } else grounded = 1;
    this.tyres.set(player.speed || 0, player.slipAngle || 0, surface, grounded, gp.weather || this._weather);
    this.tyres.update(d);

    // ---- repair spray, straight off the gameplay query (updated every fixed step)
    const rs = gp.repairSession;
    this.spray.set(!!(rs && rs.active), rs ? rs.fill01 : 0);
    this.spray.update(d);

    // ---- music intensity from combo, timer pressure and speed
    this.music.setAuto(gp.combo || 0, gp.timeRemaining == null ? 60 : gp.timeRemaining, player.speedKmh || 0);
    this.music.update(d);

    this.ambience.update(d);
    this.announcer.update(d);
    this.voices.update(d);

    // ---- horn. There is no horn event in the §5 vocabulary, so we poll the edge flag. It
    // is set for exactly one fixed step and update() runs after the steps, so at any sane
    // frame rate we see it. Guarded so one press cannot fire twice.
    if (ctx.input && ctx.input.horn && this._lastHornFrame !== ctx.time.frame) {
      this._lastHornFrame = ctx.time.frame;
      this.voices.play('horn');
    }
  }

  onResize() { /* nothing visual */ }

  // ------------------------------------------------------------------ harness

  /** ARCHITECTURE.md §7.1 — apply only the keys we own. */
  applyHarnessState(patch) {
    if (!patch) return;
    if (patch.weather) { this._weather = patch.weather; this.ambience.set(patch.weather, 1); }
    if (patch.phase) {
      this._phase = patch.phase;
      if (patch.phase === 'playing') this.music.start();
      else if (patch.phase === 'result' || patch.phase === 'title') this.music.stop();
    }
    if (patch.repairSession) {
      this.spray.set(!!patch.repairSession.active, patch.repairSession.fill01 || 0);
    }
    if (patch.combo != null || patch.timeRemaining != null) {
      this.music.setAuto(patch.combo || 0,
        patch.timeRemaining == null ? 60 : patch.timeRemaining, 0);
    }
    if (patch.announce && patch.announce.style) {
      // The harness forces the UI banner; mirror it into the announcer model so the queue
      // state a critic reads matches what is on screen.
      this.announcer.say(patch.announce.style === 'combo' ? 'combo4' : patch.announce.style, 1);
    }
    if (patch.muted != null) this.graph.setMuted(!!patch.muted);
  }

  /** Merged into PPM.harness.stats(). */
  harnessStats() {
    return {
      live: this.graph.live,
      disabled: this.graph.disabled,
      muted: this.graph.muted,
      frozen: this.frozen,
      bakeMs: this.bakeMs,
      buffers: this.baked ? this.baked.oneShots.size : 0,
      sampleRate: this.graph.live ? this.graph.actx.sampleRate : 0,
      engine: this.engine.stats(),
      tyres: this.tyres.stats(),
      spray: this.spray.stats(),
      music: this.music.stats(),
      ambience: this.ambience.stats(),
      announcer: this.announcer.stats(),
      voices: this.voices.stats(),
    };
  }

  /**
   * ARCHITECTURE.md §7.3. Audio produces no pixels, so the only thing freezing has to
   * guarantee is that we neither throw nor influence anything: we go silent and every model
   * clock is pinned to ctx.time.sim, after which they advance only by the fixed dt the game
   * hands us (game.js passes SIM_DT once frozen). Our state is then a pure function of the
   * step count, which is what makes two capture runs agree exactly.
   */
  freezeClocks() {
    this.frozen = true;
    this.graph.setMuted(true);
    this.music.stop();
    this.voices.stopAll();
    this.announcer.reset();
    const t = this.ctx.time.sim;
    this.music.clock = t;
    this.announcer.clock = t;
    this.voices.clock = t;
  }

  /** ARCHITECTURE.md §7.2b — re-fork, do not reuse the init() fork. */
  async reset(opts) {
    this.rng = this.ctx.rng.fork('audio');
    for (let i = 0; i < RAND_TABLE_SIZE; i++) this.randValues[i] = this.rng.next();
    this.bakeRand.seekTo(0);
    this.musicRand.seekTo(40009);
    this.sfxRand.seekTo(50021);

    this.engine.reset();
    this.tyres.reset();
    this.spray.reset();
    this.announcer.reset();
    this.music.reset();
    this.ambience.reset();
    this.voices.reset();

    this._weather = (opts && opts.weather) || 'thaw';
    this._phase = 'title';
    this._lastHornFrame = -1;
    this.ambience.set(this._weather, 1);

    // The bank is a pure function of the table, so if audio is live, re-bake it against the
    // new seed. Nothing leaks: the previous AudioBuffers are dropped wholesale.
    if (this.graph.live) {
      this.baked = bakeBank(this.bakeRand, T.renderRate);
      this.bakeMs = +this.baked.ms.toFixed(1);
      this.graph.baked = this.baked;
      this.graph.buffers = toAudioBuffers(this.graph.actx, this.baked);
    }
  }

  dispose() {
    if (this._subs) {
      for (const [t, fn] of this._subs) this.ctx.bus.off(t, fn);
      this._subs = null;
    }
    if (this.voices) this.voices.stopAll();
    if (this.music) this.music.dispose();
    if (this.graph) this.graph.dispose();
    this.baked = null;
  }
}
